import { BaseWebComponentController, IListenerConfig, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { WebAccessControlSchedulerComponent } from '../component/WebAccessControlSchedulerComponent';
import { WebAccessControlSchedulerView } from '../view/WebAccessControlSchedulerView';
import { templateWebAccessControlScheduler } from '../view/template';
import { WebAccessControlSchedulerRepository } from '../repository/WebAccessControlSchedulerRepository';
import { Dependencies, ScheduleGridEvents, ScheduleItem } from '../types';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { GridEvents } from '../../../grid/types/common';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { showErrorToast, showSuccessToast } from '../../../ui/gzToastNotification';
import { Rights } from '@nirvana/shared/constants';
import { EditPolicyAssignmentPanelWebComponent } from '../../editPolicyAssignmentPanel';
import { EditPolicyAssignmentEvents } from '../../editPolicyAssignmentPanel/types';
import { ConfigurationProfilesWebComponent } from '../../configurationProfiles';
import { translateFn } from '../../../utils/translator.helper';
import { htmlEncode } from '../../../utils/escape.utils';

export class WebAccessControlSchedulerController extends BaseWebComponentController {
  public component: WebAccessControlSchedulerComponent;
  public view: WebAccessControlSchedulerView;
  public dataRepository: WebAccessControlSchedulerRepository;
  public viewConstructor: typeof WebAccessControlSchedulerView;
  public dataRepositoryConstructor: typeof WebAccessControlSchedulerRepository;
  public dependencies: Dependencies;
  private selectedItemsData: Array<ScheduleItem> = [];

  constructor(component: WebAccessControlSchedulerComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: WebAccessControlSchedulerView,
      viewTemplate: templateWebAccessControlScheduler,
      dataRepositoryConstructor: WebAccessControlSchedulerRepository,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$webAccessControlSchedulerForm,
        event: ScheduleGridEvents.SAVE_SCHEDULE,
        callback: this.refreshGrid,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: this.onSelectionChanged,
      },
      {
        element: this.view.dom.$grid,
        event: ScheduleGridEvents.CLICK_ON_SCHEDULE_NAME,
        callback: this.onScheduleNameClick,
      },
      {
        element: this.view.dom.$grid,
        event: ScheduleGridEvents.CLICK_ON_IN_POLICIES,
        callback: this.onInPoliciesClick,
      },
      {
        element: this.view.dom.$deleteBtn,
        event: 'click',
        callback: this.onDeleteButtonClick,
      },
      {
        element: this.view.dom.$addScheduleBtn,
        event: 'click',
        callback: this.onAddScheduleClick,
      }
    ];
  }

  private onSelectionChanged(ev: WebComponentEvent) {
    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.view.dom.$deleteBtn.disabled = true;
      return;
    }

    this.selectedItemsData = ev.detail.selectedItemsData;
    let disabled = true;
    if (!isEmpty(this.selectedItemsData) && Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      disabled = false;
    }

    for (const element of this.selectedItemsData) {
      const schedule: ScheduleItem = element;
      if (!schedule.allowChangeByOtherUsers && schedule.created?.userId !== Application.Main.user._id) {
        disabled = true;
        break;
      }
    }

    this.view.dom.$deleteBtn.disabled = disabled;
  }

  private onScheduleNameClick(event: CustomEvent) {
    this.view.showWebAccessControlSchedulerForm(event.detail.rowData.item);
  }

  private async onInPoliciesClick(event: CustomEvent) {
    const { companyId, schedule } = event.detail;
    let response;
    let policyIdsAssignedToSchedule: string[] = [];
    try {
      response = await this.dataRepository.getPoliciesByWebAccessControlScheduleId(companyId, schedule._id);
    } catch (error) {
      console.error(error.message);
    }

    if (!response || !response.success) {
      return;
    }

    policyIdsAssignedToSchedule = response.data;
    await this.showEditPolicyAssignmentPanel(companyId, schedule, policyIdsAssignedToSchedule);
  }

  private async showEditPolicyAssignmentPanel(companyId: string, schedule, policyIdsAssignedToSchedule: string[]) {
    const editPolicyAssignmentPanel = new EditPolicyAssignmentPanelWebComponent(this.dependencies);
    editPolicyAssignmentPanel.policiesSelectorLabel = translateFn('You can assign the scheduler {SCHEDULE_NAME} only to policies that you have permission to edit and are not inherited.', {
      SCHEDULE_NAME: `<b>${htmlEncode(schedule.name)}</b>`,
    });
    editPolicyAssignmentPanel.previewMessage = translateFn('These policies already have the scheduler {SCHEDULE_NAME} assigned. Some of these policies may be read-only.', {
      SCHEDULE_NAME: `<b>${htmlEncode(schedule.name)}</b>`,
    });
    editPolicyAssignmentPanel.panelTitle = translateFn("Edit scheduler assignment");
    editPolicyAssignmentPanel.policiesSelectorProps = {
      moduleAndSection: { moduleId: "contentControl", sectionId: "web" },
      excludeInheritedPolicies: true,
      selectedPolicies: [...policyIdsAssignedToSchedule],
    };
    editPolicyAssignmentPanel.addEventListener(EditPolicyAssignmentEvents.CLICK_ON_SAVE, (e: CustomEvent) => {
      this.assignScheduleToPolicies(companyId, schedule, e.detail.selectedPolicies);
    });
    const configurationProfilesComponent = document.querySelector("gz-configuration-profiles") as ConfigurationProfilesWebComponent;
    const configurationProfilesWrapper = configurationProfilesComponent?.shadowRoot?.querySelector('.wrapper') as HTMLElement;
    editPolicyAssignmentPanel.displayPanel(configurationProfilesWrapper);
  }

  private async assignScheduleToPolicies(companyId: string, schedule, policies: string[]) {
    let response;
    try {
      response = await this.dataRepository.assignScheduleToPolicies(companyId, schedule._id, policies);
    } catch (error) {
      console.error(error.message);
    }

    if (!response || !response.success) {
      showErrorToast(translateFn('Failed to add {scheduleName} to policies. Please try again.', {scheduleName: schedule.name}));
      return;
    }

    showSuccessToast(translateFn('Successfully added {scheduleName} to policies.', {scheduleName: schedule.name}));
    this.view.dom.$grid.controller?.handleGridItemsLoad();
  }

  private async onDeleteButtonClick() {
    const windowTitle = translateFn('Delete selected schedulers');
    const windowBody = translateFn('Are you sure you want to delete the selected schedulers?');
    const windowFooter = this.getDeleteConfirmationDialogFooterHtml();
    GzDialogWebComponent.computeCustomDialog(windowBody, windowFooter, windowTitle, null, this.onDeleteButtonConfirmationDialogConfirm.bind(this));
  }

  public getDeleteConfirmationDialogFooterHtml(): string {
    return `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
        ${translateFn('DELETE')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('CANCEL')}
      </gz-button>`;
  }

  private async onDeleteButtonConfirmationDialogConfirm() {
    const unknownErrorMessage = translateFn('Schedules were not deleted. Please retry.');
    let removeError = null;
    try {
      const response = await this.dataRepository.removeRecords(this.selectedItemsData);

      if (!response) {
        removeError = unknownErrorMessage;
      }
    } catch (error) {
      removeError = error.message || unknownErrorMessage;
    }

    // when error, then display
    if (removeError) {
      showErrorToast(removeError);
      return;
    }

    showSuccessToast(translateFn('Schedules deleted successfully'));
    this.refreshGrid();
  }

  private refreshGrid() {
    this.view.dom.$grid.controller?.handleGridItemsLoad();
  }

  private onAddScheduleClick() {
    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      return;
    }
    this.view.showWebAccessControlSchedulerForm();
  }
}
